import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { getEscrowCustodial, updateCustodialSettings } from 'services'
import { svgLoading } from 'stories/assets'
import { thousandSeperator } from 'utils'

import type { ICustodialSummary, IEscrowLoanRow } from './types'

const formatAmount = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `$${thousandSeperator(value)}`
}

export const EscrowCustodial = () => {
  const [isLoading, setLoading] = useState(false)
  const [loans, setLoans] = useState<IEscrowLoanRow[]>([])
  const [totalEscrow, setTotalEscrow] = useState(0)
  const [custodial, setCustodial] = useState<ICustodialSummary | null>(null)
  const [cashGlDraft, setCashGlDraft] = useState('1060')
  const [liabilityGlDraft, setLiabilityGlDraft] = useState('2110')
  const [savingSettings, setSavingSettings] = useState(false)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getEscrowCustodial()
      setLoans(result.loans?.results || [])
      setTotalEscrow(result.loans?.totals?.escrowBalance ?? 0)
      setCustodial(result.custodial || null)
      setCashGlDraft(result.custodial?.cashGlAccountNo || '1060')
      setLiabilityGlDraft(result.custodial?.liabilityGlAccountNo || '2110')
    } catch (error) {
      console.error(error)
      setLoans([])
    }
    setLoading(false)
  }

  const onSaveSettings = async () => {
    setSavingSettings(true)
    try {
      await updateCustodialSettings({
        cashGlAccountNo: cashGlDraft,
        liabilityGlAccountNo: liabilityGlDraft,
      })
      fetch()
    } finally {
      setSavingSettings(false)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Escrow &amp; Custodial
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Segregated borrower funds. Per-loan balances pull from the Loan Subledger. Custodial cash and liability GL
        balances pull from the Trial Balance.
      </p>

      <div className="relative overflow-x-auto mb-8">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="text-xs uppercase bg-gray-100">
            <tr>
              {['Loan ID', 'Borrower', 'Escrow Type', 'Escrow Balance'].map((h) => (
                <th key={h} className="px-3 py-2 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loans.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                  No loans in the subledger yet.
                </td>
              </tr>
            )}
            {loans.map((row) => (
              <tr key={row.loanId} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{row.loanId}</td>
                <td className="px-3 py-2">{row.borrower || '-'}</td>
                <td className="px-3 py-2">{row.escrowType}</td>
                <td className="px-3 py-2 text-right">{formatAmount(row.escrowBalance)}</td>
              </tr>
            ))}
          </tbody>
          {loans.length > 0 && (
            <tfoot>
              <tr className="bg-blue-50 font-semibold border-t-2">
                <td colSpan={3} className="px-3 py-2">
                  Total escrow held
                </td>
                <td className="px-3 py-2 text-right">{formatAmount(totalEscrow)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="border border-gray-200 rounded p-4 bg-gray-50 space-y-4 max-w-2xl">
        <h2 className="text-lg font-semibold">Summary &amp; Reconciliation</h2>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="font-medium">Custodial cash GL</label>
          <input
            className="border border-gray-300 rounded px-2 py-1.5 w-24"
            value={cashGlDraft}
            onChange={(e) => setCashGlDraft(e.target.value)}
          />
          <label className="font-medium">Custodial liability GL</label>
          <input
            className="border border-gray-300 rounded px-2 py-1.5 w-24"
            value={liabilityGlDraft}
            onChange={(e) => setLiabilityGlDraft(e.target.value)}
          />
          <button
            className="bg-primary text-white rounded px-3 py-1.5 text-sm disabled:opacity-50"
            disabled={savingSettings}
            onClick={onSaveSettings}
          >
            Save GL Accounts
          </button>
        </div>

        <div className="text-sm text-gray-700 space-y-2">
          <div className="flex justify-between max-w-md">
            <span>Custodial cash (GL {custodial?.cashGlAccountNo || cashGlDraft})</span>
            <strong>{formatAmount(custodial?.custodialCashGlBalance)}</strong>
          </div>
          <div className="flex justify-between max-w-md">
            <span>Custodial liability (GL {custodial?.liabilityGlAccountNo || liabilityGlDraft})</span>
            <strong>{formatAmount(custodial?.custodialLiabilityGlBalance)}</strong>
          </div>
          <div className="flex justify-between max-w-md pt-2 border-t">
            <span className="text-green-800 font-medium">Three-way tie</span>
            <strong className={custodial?.ties ? 'text-green-700' : 'text-red-600'}>
              {custodial?.ties ? 'TIES' : 'VARIANCE'}
            </strong>
          </div>
        </div>
      </div>
    </div>
  )
}
